/*!
* # SeriouslyRAD
* http://www.seriouslyrad.com/
* 
* Copyright (c) 2016 - 2024 Seriously RAD Limited - All Rights Reserved
* Unauthorized copying of this file, via any medium is strictly prohibited
* Proprietary and confidential
*
*/

import Sizzle from "sizzle";
import { Nullable } from "./Extensions";
import { NAMESPACE } from "./_Namespace";

class ArrayHelper
{
    public static isArrayLike(array: any): array is any[]
    {
        return array && typeof array.length === "number" && array.length >= 0 && typeof array.push === "function";
    }
    public static orderBy<T>(array: T[], predicate: (item: T) => any): T[]
    {
        return Array.prototype.slice.call(array).sort((a, b) =>
        {
            if (predicate(a) < predicate(b))
                return -1;
            if (predicate(a) > predicate(b))
                return 1;
            return 0;
        });
    }
}
class MathHelper
{
    public static clamp(x: number, lower: number, upper: number)
    {
        return x <= lower ? lower : x >= upper ? upper : x;
    }
}
class ObjectHelper
{
    public static extend(target: any, object1: any, ...objectN: any[]): any
    public static extend(deep: boolean, target: any, object1?: any, ...objectN: any[]): any
    public static extend(deepOrTarget: any, targetOrObject1: any, ...object1OrObjects: any[]): any
    {
        let
            deep = false,
            target,
            object1,
            objectN;

        if (typeof deepOrTarget === "boolean")
        {
            deep = deepOrTarget;
            target = targetOrObject1;
            objectN = object1OrObjects;
        }
        else
        {
            target = deepOrTarget;
            object1 = targetOrObject1;
            objectN = object1OrObjects;
        }

        const
            objects = object1 ? [object1] : [];

        if (objectN)
            objects.push(...objectN);

        for (let i = 0; i < objects.length; i++)
        {
            const
                source = objects[i];

            if (source === null || source === undefined)
                continue;

            if (ArrayHelper.isArrayLike(source))
            {
                if (!ArrayHelper.isArrayLike(target))
                    throw new Error("Source was an array but the target was not");

                for (let j = 0; j < source.length; j++)
                {
                    if (ArrayHelper.isArrayLike(source[j]))
                    {
                        if (!ArrayHelper.isArrayLike(target[j]))
                            target[j] = [];

                        this.extend(deep, target[j], source[j]);
                    }
                    else if (ObjectHelper.isPlainObject(source[j]))
                    {
                        if (!this.isPlainObject(target[j]))
                            target[j] = {};

                        this.extend(deep, target[j], source[j]);
                    }
                    else if (source[j] !== undefined)
                        target[j] = source[j];
                }
            }

            for (let property in source)
            {
                if (property === "__proto__")
                    continue;

                if (target === source[property])
                    continue;

                if (ArrayHelper.isArrayLike(source[property]))
                {
                    if (!ArrayHelper.isArrayLike(target[property]))
                        target[property] = [];

                    this.extend(deep, target[property], source[property]);
                }
                else if (this.isPlainObject(source[property]))
                {
                    if (!this.isPlainObject(target[property]))
                        target[property] = {};

                    if (deep)
                        this.extend(deep, target[property], source[property]);
                    else
                        target[property] = source[property];
                }
                else if (typeof source[property] === "function")
                {
                    if (Object.prototype.hasOwnProperty.call(source, property))
                        target[property] = source[property];
                }
                else if (source[property] !== undefined)
                    target[property] = source[property];
            }
        }

        return target;
    }
    public static fromJson(s: string, reviver?: (this: any, key: string, value: any) => any): any
    {
        return JSON.parse(s, reviver);
    }
    public static getPropertyNames(obj: any)
    {
        const
            result = new Set<string>();

        while (obj)
        {
            Object.getOwnPropertyNames(obj).forEach(item => result.add(item));
            obj = Object.getPrototypeOf(obj);
        }
        return [...result];
    }
    public static hasProperty(obj: any, propertyName: string)
    {
        return this.getPropertyNames(obj).indexOf(propertyName) >= 0;
    }
    public static isPlainObject(obj: any)
    {
        return (
            obj !== null
            && typeof obj === "object"
            && obj.constructor === Object
            && Object.prototype.toString.call(obj) === "[object Object]"
        );
    }
}
class StringHelper
{
    public static fromDashed(s: string)
    {
        if (this.isNullOrUndefined(s))
            return s;

        return s.replace(/-(.)/g, function (_, group)
        {
            return group.toUpperCase();
        });
    }
    public static isNull(s: Nullable<string | undefined>)
    {
        return s === null;
    }
    public static isNullOrUndefined(s: Nullable<string | undefined>)
    {
        return this.isNull(s) || this.isUndefined(s);
    }
    public static isUndefined(s: Nullable<string | undefined>)
    {
        return s === undefined;
    }
    public static toDashed(s: string)
    {
        if (this.isNullOrUndefined(s))
            return s;

        return s.replace(/([a-z])([A-Z])/g, "$1-$2").toLowerCase();
    }
}

// #region Check for Passive Event support

let
    supportsPassiveEvents = false;

try
{
    const
        opts = Object.defineProperty({}, "passive", {
            get: function ()
            {
                supportsPassiveEvents = true;
                return undefined;
            }
        }),
        listener = (event: MouseEvent) => undefined;

    window.addEventListener("click", listener, opts);
    window.removeEventListener("click", listener, opts);
}
catch (e) { /* Intentionally empty */ }

// #endregion

interface IEventHandler
{
    data: any;
    handler: Function;
    invoked?: boolean;
    namespace?: string;
    once?: boolean;
    selector: string;
    targetSelf: any;
    thisArg: any;
}

interface IEventHandlersByEventName
{
    handlers: IEventHandler[];
}

interface IElementEventHandlersByEventName extends IEventHandlersByEventName
{
    elementEventHandler: EventListenerOrEventListenerObject;
}

interface IEventHandlersByTarget<T extends IEventHandlersByEventName = IEventHandlersByEventName>
{
    [eventName: string]: T
}

export interface IJNativeEvent<TEvent extends Event = Event>
{
    currentTarget?: EventTarget;
    data?: any;
    detail?: any;
    namespace?: string;
    originalEvent: TEvent;
    path: EventTarget[];
    preventDefault(): void;
    stopPropagation(): void;
    target?: EventTarget;
    type?: string;
}

class AjaxSetupOptions
{
    public static default = new AjaxSetupOptions();

    public beforeSend: any = undefined;
    public timeout: number = 30000;
}

type ElementType = Document | DocumentFragment | Element | Window | object;

function parseXml(data: string): Document | undefined
{
    let
        xml;

    try { xml = (new window.DOMParser()).parseFromString(data, "text/xml"); }
    catch (e) { /* Intentionally empty */ }

    const
        parserErrorElement = xml && xml.getElementsByTagName("parsererror")[0];

    if (xml && !parserErrorElement)
        return xml;
}

function $$Function<TElementType extends ElementType>(): JNative<TElementType>
function $$Function<TElementType extends ElementType>(readyFunction: () => void): JNative<TElementType>
function $$Function<TElementType extends HTMLElement>(html: string): JNative<TElementType>
function $$Function<TElementType extends ElementType>(arrayOrElementOrFunctionOrHtmlOrSelector?: TElementType[] | TElementType): JNative<TElementType>
function $$Function(arrayOrElementOrFunctionOrHtmlOrSelector?: ElementType[] | ElementType | (() => void) | string, context?: Document | DocumentFragment | Element | string): JNative<ElementType>
function $$Function(arrayOrElementOrFunctionOrHtmlOrSelector?: ElementType[] | ElementType | (() => void) | string, context?: Document | DocumentFragment | Element | string): JNative<ElementType>
{
    let
        arr;

    if (!arrayOrElementOrFunctionOrHtmlOrSelector)
        arr = [];
    else
    {
        if (typeof arrayOrElementOrFunctionOrHtmlOrSelector === "function")
            return JNative.create().ready(arrayOrElementOrFunctionOrHtmlOrSelector as () => void);
        else if (ArrayHelper.isArrayLike(arrayOrElementOrFunctionOrHtmlOrSelector))
            arr = arrayOrElementOrFunctionOrHtmlOrSelector;
        else if (typeof arrayOrElementOrFunctionOrHtmlOrSelector === "string")
        {
            const
                trimmedString = arrayOrElementOrFunctionOrHtmlOrSelector.trim();

            if (trimmedString.length >= 3 && trimmedString[0] === "<" && trimmedString[trimmedString.length - 1] === ">")
            {
                const tempElement = document.createElement("div");
                tempElement.insertAdjacentHTML("beforeend", arrayOrElementOrFunctionOrHtmlOrSelector);
                arr = Array.from(tempElement.children);
            }
            else
            {
                if (typeof context === "string")
                    context = parseXml(context);

                arr = Sizzle(arrayOrElementOrFunctionOrHtmlOrSelector, context);
            }
        }
        else
            arr = [arrayOrElementOrFunctionOrHtmlOrSelector];
    }

    return JNative.create(arr);
}

export const $$ = $$Function;

interface JNativeSelectors extends Sizzle.Selectors
{
}

class ElementEventManager
{
    public static documentReadyEventHandlers: any[] = [];

    private static _eventHandlers = new WeakMap<EventTarget, IEventHandlersByTarget<IElementEventHandlersByEventName>>();
    private static _htmlEventNames =
        Object.keys(window)
            .filter(item => /\bon/.test(item))
            .map(item => item.substring(2))
            .reduce((a, b) => ({ ...a, [b]: true }), {});

    public static domContentLoadedHandler()
    {
        document.removeEventListener("DOMContentLoaded", ElementEventManager.domContentLoadedHandler);
        ElementEventManager.documentReadyEventHandlers.forEach(item => { item(); });
        ElementEventManager.documentReadyEventHandlers = [];
    }
    public static eventHandlerCreate(eventName: string)
    {
        return function (evt: Event)
        {
            const
                eventTarget = evt.currentTarget ?? evt.target ?? ((window as any).cordova ? document : null),
                eventHandlers = eventTarget ? ElementEventManager.eventHandlersGet(eventTarget) : undefined;

            if (!eventHandlers || !eventHandlers[eventName] || !eventHandlers[eventName].handlers.length)
                return;

            // Clone (Handlers may modify list during execution) and call those with selectors first
            const
                eventHandlersClone = ArrayHelper.orderBy(eventHandlers[eventName].handlers, (item: { selector: string; }) => !item.selector),
                event: IJNativeEvent = {
                    originalEvent: evt,
                    path: [],
                    preventDefault: function () { this.originalEvent.preventDefault(); },
                    stopPropagation: function () { this.originalEvent.stopPropagation(); },
                },
                path = (evt as any)["path"] ?? (evt.composedPath && evt.composedPath()) ?? [];

            if (!path.length)
                path.push(evt.currentTarget ?? evt.target);

            for (let property in evt)
            {
                if (typeof (evt as any)[property] !== "function")
                    (event as any)[property] = (evt as any)[property];
            }

            event.target = path[0];

            if (!event.currentTarget && eventTarget)
                event.currentTarget = eventTarget;

            // event.currentTarget = element that the event is bound on
            // event.target = element that triggered the event

            for (let pathItem of path)
            {
                for (let handlerInfo of eventHandlersClone)
                {
                    if (handlerInfo.once && handlerInfo.invoked)
                        continue;

                    if (handlerInfo.selector)
                    {
                        if (!pathItem.matches)
                            continue;

                        const
                            target = evt.currentTarget ?? evt.target;

                        if (!(target instanceof Element || target instanceof Document || target instanceof DocumentFragment))
                            continue;

                        const
                            matchingElements = Sizzle(handlerInfo.selector, target);

                        if (matchingElements.indexOf(pathItem) < 0)
                            continue;

                        event.currentTarget = pathItem;
                    }
                    else if (pathItem !== path[0])
                        break;

                    event.data = JNativeStatic.extend({}, event.data, handlerInfo.data);
                    event.namespace = handlerInfo.namespace;

                    handlerInfo.handler.apply(handlerInfo.thisArg ?? event.currentTarget, [event, (evt instanceof CustomEvent) ? (evt as CustomEvent).detail : undefined]);
                    handlerInfo.invoked = true;
                }

                if (evt.cancelBubble)
                    return;
            }

            if (event.type && eventHandlers[event.type])
            {
                for (let i = eventHandlers[event.type].handlers.length - 1; i >= 0; i--)
                {
                    const
                        handlerInfo = eventHandlers[event.type].handlers[i];

                    if (handlerInfo.once && handlerInfo.invoked)
                        eventHandlers[event.type].handlers.splice(i, 1);
                }
            }

        };
    }
    public static eventHandlerOff(element: EventTarget, eventName: string, selector: string, data: any, handler: Function)
    {
        const
            targetSelf = data?.self,
            eventHandlers = this.eventHandlersGet(element);

        if (!eventHandlers[eventName])
            return;

        eventHandlers[eventName].handlers =
            eventHandlers[eventName].handlers
                .filter(
                    item =>
                        !(
                            (!targetSelf || item.targetSelf === targetSelf)
                            && (!selector || item.selector === selector)
                            && (!handler || item.handler === handler)
                        )
                );

        if (!eventHandlers[eventName].handlers.length)
        {
            const
                eventNameIndexOfDot = eventName.indexOf(".");

            let
                elementEventName = eventNameIndexOfDot > -1 ? eventName.substring(0, eventNameIndexOfDot) : eventName;

            if (!(this._htmlEventNames as any)[elementEventName])
                elementEventName = eventName;

            element.removeEventListener(
                elementEventName,
                eventHandlers[eventName].elementEventHandler
            );

            delete eventHandlers[eventName];
        }
    }
    public static eventHandlerOffAll(element: EventTarget)
    {
        const
            eventHandlers = this.eventHandlersGet(element);

        for (let eventName of Object.keys(eventHandlers))
        {
            const
                eventNameIndexOfDot = eventName.indexOf(".");

            let
                elementEventName = eventNameIndexOfDot > -1 ? eventName.substring(0, eventNameIndexOfDot) : eventName;

            if (!(this._htmlEventNames as any)[elementEventName])
                elementEventName = eventName;

            element.removeEventListener(
                elementEventName,
                eventHandlers[eventName].elementEventHandler
            );

            delete eventHandlers[eventName];
        }
    }
    public static eventHandlerOn(element: EventTarget, eventName: string, selector: string, data: any, handler: Function, once: boolean, thisArg: any)
    {
        const
            targetSelf = data?.self,
            eventHandlers = this.eventHandlersGet(element),
            alreadyRegistered =
                eventHandlers[eventName]?.handlers
                && eventHandlers[eventName].handlers
                    .some((item: { targetSelf: any; selector: string; handler: Function; }) =>
                    {
                        return (
                            item.targetSelf === targetSelf
                            && item.selector === selector
                            && item.handler === handler
                        );
                    });

        if (alreadyRegistered)
            return;

        const
            handlerInfo: IEventHandler =
            {
                targetSelf: targetSelf,
                selector: selector,
                data: data,
                handler: handler,
                once: once ?? false,
                thisArg: thisArg
            };

        if (!eventHandlers[eventName])
            eventHandlers[eventName] = { elementEventHandler: this.eventHandlerCreate(eventName), handlers: [] };

        if (!eventHandlers[eventName].handlers.length)
        {
            const
                eventNameIndexOfDot = eventName.indexOf(".");

            let
                elementEventName = eventNameIndexOfDot > -1 ? eventName.substring(0, eventNameIndexOfDot) : eventName;

            if (!(this._htmlEventNames as any)[elementEventName])
                elementEventName = eventName;

            const
                capture = ["mouseenter", "mouseleave"].indexOf(elementEventName) >= 0,
                shouldUsePassiveEvent =
                    elementEventName === "scroll"
                    || elementEventName.substring(0, 5) === "touch";

            element.addEventListener(
                elementEventName,
                eventHandlers[eventName].elementEventHandler,
                shouldUsePassiveEvent && supportsPassiveEvents ? { passive: true } : capture
            );
        }

        eventHandlers[eventName].handlers.push(handlerInfo);
    }
    public static eventHandlersGet(element: EventTarget)
    {
        if (!element)
            return {};

        let
            eventHandlers = this._eventHandlers.get(element);

        if (!eventHandlers)
        {
            eventHandlers = {};
            this._eventHandlers.set(element, eventHandlers);
        }

        return eventHandlers;
    }
    public static eventHandlerTrigger(element: EventTarget, eventName: string, eventData: any)
    {
        if (eventName === "focus" && element instanceof HTMLElement)
            element.focus();
        //if (element[eventName])
        //    element[eventName]();
        element.dispatchEvent(new CustomEvent(eventName, { bubbles: true, detail: eventData }));
    }
    public static eventHandlerTriggerHandler(element: EventTarget, eventName: string, eventData: any)
    {
        if (eventName === "focus" && element instanceof HTMLElement)
            element.focus();
        //if (element[eventName])
        //    element[eventName]();
        element.dispatchEvent(new CustomEvent(eventName, { bubbles: false, detail: eventData }));
    }

    static {
        if (document.readyState === "loading")
            document.addEventListener("DOMContentLoaded", ElementEventManager.domContentLoadedHandler);
    }
}

class ObjectEventManager
{
    private static _eventHandlers = new WeakMap<any, IEventHandlersByTarget>()

    public static eventHandlerOff(obj: any, eventName: string, selector: string, data: any, handler: Function)
    {
        const
            targetSelf = data?.self,
            eventHandlers = this.eventHandlersGet(obj);

        if (!eventHandlers[eventName])
            return;

        eventHandlers[eventName].handlers =
            eventHandlers[eventName].handlers
                .filter(
                    item =>
                        !(
                            (!targetSelf || item.targetSelf === targetSelf)
                            && (!selector || item.selector === selector)
                            && (!handler || item.handler === handler)
                        )
                );

        if (!eventHandlers[eventName].handlers.length)
            delete eventHandlers[eventName];
    }
    public static eventHandlerOffAll(obj: any)
    {
        const
            eventHandlers = this.eventHandlersGet(obj);

        for (let key of Object.keys(eventHandlers))
            delete eventHandlers[key];
    }
    public static eventHandlerOn(obj: any, eventName: string, selector: string, data: any, handler: Function, once: boolean, thisArg: any)
    {
        const
            targetSelf = data?.self,
            eventHandlers = this.eventHandlersGet(obj),
            alreadyRegistered =
                eventHandlers[eventName]?.handlers
                && eventHandlers[eventName].handlers
                    .some((item: { targetSelf: any; selector: string; handler: Function; }) =>
                    {
                        return (
                            item.targetSelf === targetSelf
                            && item.selector === selector
                            && item.handler === handler
                        );
                    });

        if (alreadyRegistered)
            return;

        const
            handlerInfo =
            {
                targetSelf: targetSelf,
                selector: selector,
                data: data,
                handler: handler,
                once: once,
                thisArg: thisArg
            };

        if (!eventHandlers[eventName])
            eventHandlers[eventName] = { handlers: [] };

        eventHandlers[eventName].handlers.push(handlerInfo);
    }
    public static eventHandlerTrigger(obj: any, eventName: string, eventData: any, bubble: boolean)
    {
        const
            nativeEvent = new Event(eventName),
            event: IJNativeEvent = {
                currentTarget: obj,
                originalEvent: nativeEvent,
                path: [],
                preventDefault: function () { this.originalEvent.preventDefault(); },
                stopPropagation: function () { this.originalEvent.stopPropagation(); },
                target: obj,
            };

        for (let property in nativeEvent)
        {
            if (typeof (nativeEvent as any)[property] !== "function")
                (event as any)[property] = (nativeEvent as any)[property];
        }

        let
            currentObject = obj;

        while (currentObject)
        {
            event.path.push(currentObject);

            this.eventHandlerTriggerHandlers(currentObject, event, eventData);

            if (!bubble)
                break;

            let
                nextObject,
                nextElement = currentObject.$$element?.get ? currentObject.$$element.get()?.get(0) : undefined;

            while (nextElement)
            {
                nextElement = nextElement.parentElement;

                if (!nextElement)
                    break;

                if (nextElement.classList.contains("rad-control") && nextElement.nodeName !== "HTML") // TODO: BAM
                {
                    const
                        radControl = JNativeStatic.radControl(nextElement);

                    if (radControl)
                    {
                        nextObject = radControl;
                        break;
                    }
                }
            }

            currentObject = nextObject;
        }
    }
    public static eventHandlerTriggerHandlers(obj: any, event: IJNativeEvent, eventData: any)
    {
        const
            eventHandlers = this.eventHandlersGet(obj);

        if (!eventHandlers || !event.type || !eventHandlers[event.type] || !eventHandlers[event.type].handlers.length)
            return;

        // Clone (Handlers may modify list during execution) and call those with selectors first
        const
            eventHandlersClone = ArrayHelper.orderBy(eventHandlers[event.type].handlers, (item: { selector: any; }) => !item.selector);

        // event.currentTarget = element that the event is bound on
        // event.target = element that triggered the event

        for (let pathItem of event.path)
        {
            for (let handlerInfo of eventHandlersClone)
            {
                if (handlerInfo.once && handlerInfo.invoked)
                    continue;

                event.currentTarget = obj;

                if (handlerInfo.selector)
                {
                    const
                        selector =
                            handlerInfo.selector
                                .split(",")
                                .map((item: string) =>
                                {
                                    const
                                        trimmedItem = item.trim(),
                                        controlId = (event.currentTarget as any)?.controlId;

                                    if (trimmedItem && trimmedItem[0] === ">" && controlId)
                                        return `#${controlId} ${item}`;

                                    return item;
                                })
                                .join(",");

                    const
                        $$element = (pathItem as any).$$element?.get && (pathItem as any).$$element.get();

                    if (!$$element || !$$element.filter(selector).length)
                        continue;

                    event.currentTarget = pathItem;
                }
                else if (pathItem !== event.path[0])
                    break;

                event.data = JNativeStatic.extend({}, event.data, handlerInfo.data);

                handlerInfo.handler.apply(handlerInfo.thisArg ?? event.currentTarget, [event, eventData]);
                handlerInfo.invoked = true;
            }

            if (event.originalEvent.cancelBubble)
                return;
        }

        if (eventHandlers[event.type])
        {
            for (let i = eventHandlers[event.type].handlers.length - 1; i >= 0; i--)
            {
                const
                    handlerInfo = eventHandlers[event.type].handlers[i];

                if (handlerInfo.once && handlerInfo.invoked)
                    eventHandlers[event.type].handlers.splice(i, 1);
            }
        }
    }
    public static eventHandlersGet(obj: any)
    {
        let
            eventHandlers = this._eventHandlers.get(obj);

        if (!eventHandlers)
        {
            eventHandlers = {};
            this._eventHandlers.set(obj, eventHandlers);
        }

        return eventHandlers;
    }
}

export interface CreateConfig
{
    attributes?: any;
    children?: (CreateConfig | Node)[];
    data?: any;
    nodeName?: string;
    text?: string;
}

class Animator
{
    public static animationQueues = {
        default: new WeakMap()
    };
    public static numericToPixelProperties = [
        "background-position",
        "border",
        "border-bottom",
        "border-bottom-width",
        "border-left",
        "border-left-width",
        "border-right",
        "border-right-width",
        "border-spacing",
        "border-top",
        "border-top-width",
        "border-width",
        "bottom",
        "font",
        "font-size",
        "height",
        "left",
        "letter-spacing",
        "line-height",
        "margin",
        "margin-bottom",
        "margin-left",
        "margin-right",
        "margin-top",
        "max-height",
        "max-width",
        "min-height",
        "min-width",
        "outline",
        "outline-width",
        "padding",
        "padding-bottom",
        "padding-left",
        "padding-right",
        "padding-top",
        "right",
        "text-indent",
        "top",
        "vertical-align",
        "width",
        "word-spacing"
    ];

    private static parseCssSize(s: string)
    {
        const
            results = s.match(/(-?[\d.]+)([a-z%]*)/);

        return {
            value: results ? parseFloat(results[1]) : 0,
            unit: results ? results[2] : "px"
        };
    }

    private _obj: any;
    private _objAnimate: any;
    private _propertyName: string;
    private _targetValue: number;
    private _targetUnit: string;
    private _duration: number;
    private _easingFunction: string;

    private _objElement?: Element;
    private _stop = false;
    private _finish = false;

    constructor(obj: any, propertyName: string, targetValue: number | string, duration: number, easingFunction: string)
    {
        this._obj = obj;
        this._propertyName = propertyName;

        if (this._obj instanceof Document && ObjectHelper.hasProperty(this._obj.body.style, this._propertyName))
        {
            this._objAnimate = this._obj.body.style;
            this._objElement = this._obj.body;
        }
        else if (this._obj instanceof Document && ObjectHelper.hasProperty(this._obj.body, this._propertyName))
        {
            this._objAnimate = this._obj.body;
            this._objElement = this._obj.body;
        }
        else if (this._obj instanceof HTMLElement && ObjectHelper.hasProperty(this._obj.style, this._propertyName))
        {
            this._objAnimate = this._obj.style;
            this._objElement = this._obj;
        }
        else
            this._objAnimate = this._obj;

        const
            propertyNameDashed = StringHelper.toDashed(this._propertyName),
            trimmedTargetValue = `${targetValue}`.trim(),
            size = Animator.parseCssSize(trimmedTargetValue);

        this._targetValue = size.value;
        this._targetUnit = size.unit;

        if (!this._targetUnit && Animator.numericToPixelProperties.indexOf(propertyNameDashed) >= 0)
            this._targetUnit = "px";

        this._duration = duration || 400;
        this._easingFunction = easingFunction || "easeInOutQuart";
    }

    start()
    {
        let
            startDate = +new Date(),
            originalValueString = this._objAnimate[this._propertyName];

        if (!originalValueString)
        {
            if (this._objAnimate instanceof CSSStyleDeclaration && this._objElement)
            {
                const
                    computedStyle = getComputedStyle(this._objElement);

                originalValueString = computedStyle[this._propertyName as keyof CSSStyleDeclaration];
            }
        }

        if (!originalValueString)
            originalValueString = "0px";

        let
            originalValue = parseFloat(originalValueString),
            currentValue = originalValue;

        if (currentValue === this._targetValue)
            return Promise.resolve();

        let
            resolveOuter: (value?: unknown) => void;

        const
            tick = () =>
            {
                const
                    factor =
                        this._finish
                            ? 1
                            : (JNativeStatic.easing as any)[this._easingFunction](MathHelper.clamp((new Date().getTime() - startDate) / this._duration, 0, 1));

                currentValue = originalValue + factor * (this._targetValue - originalValue);

                this._objAnimate[this._propertyName] =
                    this._targetUnit
                        ? `${currentValue}${this._targetUnit}`
                        : currentValue;

                if (factor === 1)
                {
                    resolveOuter();
                    return;
                }

                if (this._stop)
                {
                    resolveOuter("Animation stopped");
                    return;
                }

                requestAnimationFrame(tick);
            };

        return new Promise((resolve, reject) =>
        {
            resolveOuter = resolve;
            tick();
        });
    }
    stop()
    {
        this._stop = true;
    }
    finish()
    {
        this._finish = true;
    }
}

class JNativeHelper
{
    public static dataMap = new WeakMap<Element, any>();
    public static jNativeSelectors: JNativeSelectors = Sizzle.selectors;
    public static radControlMap = new WeakMap<Element, any>();

    public static createNextSelector<TElementType extends ElementType = ElementType>(current: JNative, next: any[]): JNative<TElementType>
    {
        return JNative.create<TElementType>(next, current);
    }
    public static getElement(item: any): Element | undefined
    {
        if (item instanceof Element)
            return item;
        else if (item instanceof Document)
            return item.body;
    }
    public static getElements(array: any[], predicate?: (element: Element) => boolean): Element[]
    {
        return (
            Array.prototype.filter.call(
                array.map(item => this.getElement(item)),
                item => !!item && (!predicate || predicate(item))
            )
        );
    }
    public static getHtmlElement(item: any): HTMLElement | undefined
    {
        if (item instanceof HTMLElement)
            return item;
        else if (item instanceof Document)
            return item.body;
    }
    public static getHtmlElements(array: any[], predicate?: (element: HTMLElement) => boolean): HTMLElement[]
    {
        return (
            Array.prototype.filter.call(
                array.map(item => this.getHtmlElement(item)),
                item => !!item && (!predicate || predicate(item))
            )
        );
    }
    public static getElementFromArrayOrElementOrSelector(arrayOrElementOrSelector: any[] | Element | string)
    {
        if (arrayOrElementOrSelector instanceof Element)
            return arrayOrElementOrSelector;

        if (typeof arrayOrElementOrSelector === "string")
            arrayOrElementOrSelector = Sizzle(arrayOrElementOrSelector);

        if (ArrayHelper.isArrayLike(arrayOrElementOrSelector))
        {
            const
                nodes = JNativeHelper.getElements(arrayOrElementOrSelector);

            if (nodes && nodes.length)
                return nodes[0];
        }
    }
    public static getNode(item: any)
    {
        if (item instanceof Node)
            return item;
    }
    public static getNodes(array: any[], predicate?: (element: Node) => boolean): Node[]
    {
        return (
            Array.prototype.filter.call(
                array.map(item => this.getNode(item)),
                item => !!item && (!predicate || predicate(item))
            )
        );
    }
    public static getNodeFromArrayOrElementOrSelector(arrayOrElementOrSelector: any[] | Element | string)
    {
        if (arrayOrElementOrSelector instanceof Node)
            return arrayOrElementOrSelector;

        if (typeof arrayOrElementOrSelector === "string")
            arrayOrElementOrSelector = Sizzle(arrayOrElementOrSelector);

        if (ArrayHelper.isArrayLike(arrayOrElementOrSelector))
        {
            const
                nodes = this.getNodes(arrayOrElementOrSelector);

            if (nodes && nodes.length)
                return nodes[0];
        }
    }

    static {
        JNativeHelper.jNativeSelectors.pseudos.hidden = (elem: Element) => !JNativeStatic.expr.pseudos.visible(elem);
        JNativeHelper.jNativeSelectors.pseudos.visible = (elem: Element) =>
        {
            if (!(elem instanceof HTMLElement))
                return false;

            return !!(elem.offsetWidth || elem.offsetHeight || elem.getClientRects().length);
        };
    }
}

export class JNative<TElementType extends ElementType = ElementType> extends Array<TElementType>
{
    public static classNamespace = NAMESPACE + "JNative";
    public static className = "JNative";
    public static get classFullName() { return `${this.classNamespace}.${this.className}`; }

    public static create<TElementType extends ElementType = ElementType>(arr?: TElementType[], previous?: JNative): JNative<TElementType>
    {
        const
            jNative = Object.create(JNative.prototype) as JNative<TElementType>;

        jNative._previous = previous;

        if (arr)
            jNative.push(...arr);

        return jNative;
    }

    private _previous?: JNative;
    private _promise?: Promise<any>;

    protected _triggerNativeEvent(name: string, eventDataOrHandler?: any | Function, handler?: Function)
    {
        let
            eventData;

        if (typeof eventDataOrHandler === "function")
            handler = eventDataOrHandler;
        else
            eventData = eventDataOrHandler;

        if (eventData === undefined && handler === undefined)
            return this.trigger(name);

        if (handler)
            return this.on(name, eventData, handler);

        return this;
    }

    add(itemOrArray?: Nullable<TElementType | TElementType[] | NodeListOf<Element>>): JNative<TElementType>
    {
        if (!itemOrArray)
            return JNativeHelper.createNextSelector(this, [...this]);

        if (ArrayHelper.isArrayLike(itemOrArray))
            return JNativeHelper.createNextSelector(this, [...this, ...itemOrArray]);

        return JNativeHelper.createNextSelector(this, [...this, itemOrArray]);
    }
    addBack(selector?: string): JNative<TElementType>
    {
        const
            items =
                this._previous
                    ?
                    (
                        selector
                            ? this._previous.filter(selector)
                            : this._previous
                    )
                    : JNative.create();

        if (items.length)
            return JNativeHelper.createNextSelector(this, [...this, ...items]);

        return this;
    }
    addClass(className: string): JNative<TElementType>
    {
        if (className)
        {
            for (let element of JNativeHelper.getElements(this))
            {
                if ((element.classList as any)?.add)
                {
                    DOMTokenList.prototype.add.apply(
                        element.classList,
                        className
                            .split(" ")
                            .filter(item => !!item)
                    );
                    continue;
                }

                const
                    originalClassName = element.className,
                    classList =
                        originalClassName
                            ? originalClassName.split(" ").filter(item => !!item)
                            : [];

                className.split(" ")
                    .filter(item => !!item && classList.indexOf(item) === -1)
                    .forEach(item => { classList.push(item); })

                const
                    currentClassName = classList.join(" ");

                if (currentClassName !== originalClassName)
                    element.className = currentClassName;
            }
        }

        return this;
    }
    after(something: any)
    {
        // TODO
    }
    animate(properties: any, duration?: number, easing?: string, complete?: Function): JNative<TElementType>
    {
        const
            promises: Promise<any>[] = [],
            queue = "default";

        for (let node of this)
        {
            for (let property in properties)
            {
                const
                    animator = new Animator(node, property, properties[property], duration ?? 400, easing ?? "easeInOutCubic");

                if (!Animator.animationQueues[queue])
                    Animator.animationQueues[queue] = new WeakMap();

                let
                    animationQueue = Animator.animationQueues[queue].get(node);

                if (!animationQueue)
                {
                    animationQueue = [];
                    Animator.animationQueues[queue].set(node, animationQueue);
                }

                animationQueue.push(animator);

                promises
                    .push(
                        animator
                            .start()
                            .finally(() =>
                            {
                                const
                                    index = animationQueue.indexOf(animator);

                                if (index > -1)
                                    animationQueue.splice(index, 1);

                                if (complete)
                                    complete();
                            })
                    );
            }
        }

        this._promise = Promise.all(promises);

        return this;
    }
    append(arrayOrElementHtmlOrSelector?: Nullable<ElementType[] | ElementType | string>): JNative<TElementType>
    {
        if (arrayOrElementHtmlOrSelector)
        {
            if (ArrayHelper.isArrayLike(arrayOrElementHtmlOrSelector) || arrayOrElementHtmlOrSelector instanceof Node)
            {
                const
                    array = ArrayHelper.isArrayLike(arrayOrElementHtmlOrSelector) ? arrayOrElementHtmlOrSelector : [arrayOrElementHtmlOrSelector],
                    elements = JNativeHelper.getElements(this),
                    lastElement = elements.length ? elements[elements.length - 1] : undefined;

                if (lastElement)
                    lastElement.append.apply(lastElement, JNativeHelper.getNodes(array));
            }
            else if (typeof arrayOrElementHtmlOrSelector === "string")
            {
                const
                    htmlOrSelector = arrayOrElementHtmlOrSelector;

                for (let element of JNativeHelper.getElements(this))
                    element.append.apply(element, $$(htmlOrSelector) as unknown as Element[]);
            }
        }

        return this;
    }
    appendTo(arrayOrElementOrSelector?: Nullable<any[] | Element | string>): JNative<TElementType>
    {
        if (!arrayOrElementOrSelector)
            return this;

        if (!this.length)
            return this;

        const
            element = JNativeHelper.getElementFromArrayOrElementOrSelector(arrayOrElementOrSelector);

        if (element)
        {
            element
                .append
                .apply(
                    element,
                    JNativeHelper.getNodes(this)
                );
        }

        return this;
    }
    attr(propertyName: string): Nullable<string | undefined>;
    attr(propertyName: string, value: Nullable<string>): this;
    attr(propertyName: string, value?: Nullable<string>): Nullable<string | this | undefined>
    {
        if (value === undefined)
        {
            const
                elements = JNativeHelper.getElements(this);

            if (!elements.length)
                return;

            return elements[0].getAttribute(propertyName);
        }

        for (let element of JNativeHelper.getElements(this))
        {
            if (value === null)
                element.removeAttribute(propertyName);
            else
                element.setAttribute(propertyName, value);
        }

        return this;
    }
    before(something: any)
    {
        // TODO
    }
    blur(eventDataOrHandler?: any | Function, handler?: Function)
    {
        return this._triggerNativeEvent("blur", eventDataOrHandler, handler);
    }
    change(eventDataOrHandler?: any | Function, handler?: Function)
    {
        return this._triggerNativeEvent("change", eventDataOrHandler, handler);
    }
    changeTagName(tagName: string)
    {
        for (let i = 0; i < this.length; i++)
        {
            const
                node = this[i];

            if (!(node instanceof Element))
                continue;

            if (node.tagName === tagName.toUpperCase())
                continue;

            const
                attributes: any = {};

            for (let j = 0; j < node.attributes.length; j++)
                attributes[node.attributes[j].nodeName] = node.attributes[j].value;

            const
                newElement = document.createElement(tagName);

            if (attributes)
            {
                Object
                    .keys(attributes)
                    .forEach(item => { newElement.setAttribute(item, attributes[item]); });
            }

            while (node.firstChild)
                newElement.append(node.firstChild);

            if (node.parentElement)
                node.parentElement.insertBefore(newElement, node);

            node.remove();

            this[i] = newElement as TElementType;
        }
        return this;
    }
    children(selector?: string): JNative<TElementType>
    {
        return JNativeHelper.createNextSelector(
            this,
            Array.prototype.filter.call(
                JNativeHelper.getElements(this)
                    .map(item => Array.from(item.children))
                    .reduce((a, b) => a.concat(b), []),
                item => !selector || item.matches(selector)
            )
        );
    }
    click(eventDataOrHandler?: any | Function, handler?: Function)
    {
        return this._triggerNativeEvent("click", eventDataOrHandler, handler);
    }
    clone(withDataAndEvents?: boolean, deepWithDataAndEvents?: boolean): JNative<TElementType>
    {
        const
            deep = deepWithDataAndEvents === true;

        return $$(
            (JNativeHelper.getNodes(this)
                .map(item => item.cloneNode(deep)) as TElementType[])
        );
    }
    closest(selector: string): JNative<TElementType>
    {
        return JNativeHelper.createNextSelector(
            this,
            Array.prototype.filter.call(
                JNativeHelper.getElements(this)
                    .map(item => item.closest(selector)),
                item => !!item
            )
        );
    }
    contextmenu(eventDataOrHandler?: any | Function, handler?: Function)
    {
        return this._triggerNativeEvent("contextmenu", eventDataOrHandler, handler);
    }
    css(propertyName: string): string | number | undefined;
    css(propertyName: string, value: Nullable<number | string>): this;
    css(properties: any): JNative<TElementType>;
    css(propertyNameOrProperties: any, value?: Nullable<number | string>): string | number | JNative | this | undefined
    {
        if (value === undefined && typeof propertyNameOrProperties === "string")
        {
            const
                propertyName = propertyNameOrProperties,
                elements = JNativeHelper.getElements(this);

            if (!elements.length)
                return;

            return getComputedStyle(elements[0])[propertyName as keyof CSSStyleDeclaration] as string | number | undefined;
        }

        let
            style: any = {};

        if (typeof propertyNameOrProperties === "string")
            style[propertyNameOrProperties] = value;
        else
            style = propertyNameOrProperties;

        for (let element of JNativeHelper.getHtmlElements(this))
        {
            for (let property in style)
            {
                const
                    propertyNameDashed = StringHelper.toDashed(property);

                let
                    value = style[property];

                if (value === null)
                {
                    element.style.removeProperty(propertyNameDashed);
                    continue;
                }

                if (typeof value === "number" && Animator.numericToPixelProperties.indexOf(propertyNameDashed) >= 0)
                    value = `${value}px`;

                element.style.setProperty(propertyNameDashed, value);
            }
        }

        return this;
    }
    data(): any;
    data(key?: string): any;
    data(obj: any, value: any): JNative<TElementType>;
    data(keyOrObj?: any, value?: any): any
    {
        const
            keyOrObjIsKey = typeof keyOrObj === "string";

        if (!keyOrObj || keyOrObjIsKey && value === undefined)
        {
            // .data()
            // .data(key)

            const
                elements = JNativeHelper.getElements(this);

            if (!elements.length)
                return;

            return keyOrObjIsKey ? JNativeStatic.data(elements[0], keyOrObj) : JNativeStatic.data(elements[0]);
        }

        if (keyOrObjIsKey && value !== undefined)
        {
            for (let element of JNativeHelper.getElements(this))
                JNativeStatic.data(element, keyOrObj, value);

            return this;
        }

        if (JNativeStatic.isPlainObject(keyOrObj))
        {
            // .data(obj)

            for (let element of JNativeHelper.getElements(this))
                JNativeStatic.data(element, keyOrObj);

            return this;
        }

        return this;
    }
    dataGet(key?: any)
    {
        return this.data(key);
    }
    dataRemove(arrayOrName?: string[] | string)
    {
        return this.removeData(arrayOrName);
    }
    dataSet(keyOrObj: any, value?: any)
    {
        return this.data(keyOrObj, value);
    }
    dblclick(eventDataOrHandler?: any | Function, handler?: Function)
    {
        return this._triggerNativeEvent("dblclick", eventDataOrHandler, handler);
    }
    detach(): JNative<TElementType>
    {
        for (let element of JNativeHelper.getElements(this))
        {
            if (element.parentElement)
                element.parentElement.removeChild(element);
        }

        return this;
    }
    each(callback: (index: number, element: TElementType) => boolean | undefined | void): JNative<TElementType>
    {
        JNativeStatic.each(this, callback);
        return this;
    }
    empty(): JNative<TElementType>
    {
        for (let element of JNativeHelper.getElements(this))
            element.textContent = "";

        return this;
    }
    eq(index: number): JNative<TElementType>
    {
        return JNativeHelper.createNextSelector(this, [this.get(index)]);
    }
    error(eventDataOrHandler?: any | Function, handler?: Function)
    {
        return this._triggerNativeEvent("error", eventDataOrHandler, handler);
    }
    fadeIn(duration: number, easingOrComplete?: string | Function, complete?: Function)
    {
        return this.fadeTo(duration, 1, easingOrComplete, complete);
    }
    fadeOut(duration: number, easingOrComplete?: string | Function, complete?: Function)
    {
        return this.fadeTo(duration, 0, easingOrComplete, complete);
    }
    fadeTo(duration: number, opacity: number | string, easingOrComplete?: string | Function, complete?: Function)
    {
        let
            easing;

        if (typeof easingOrComplete === "string")
            easing = easingOrComplete;
        else if (typeof easingOrComplete === "function")
            complete = easingOrComplete;

        return this.animate({ opacity: opacity }, duration, easing, complete);
    }
    first(): JNative<TElementType>
    {
        return this.length ? JNativeHelper.createNextSelector(this, [this[0]]) : JNativeHelper.createNextSelector(this, []);
    }
    filter<TElementType extends ElementType>(predicate: (value: ElementType, index: number, array: ElementType[]) => value is TElementType, thisArg?: any): TElementType[]
    filter(selector: string): JNative<TElementType>
    filter(selector: any)
    {
        if (typeof selector === "string")
        {
            return JNativeHelper.createNextSelector(
                this,
                JNativeHelper.getElements(this, item => item.matches(selector))
            );
        }

        return super.filter(selector);
    }
    find<TElementType extends ElementType>(predicate: (value: ElementType, index: number, obj: ElementType[]) => value is TElementType, thisArg?: any): TElementType | undefined
    find(selector: string): JNative<TElementType>
    find(selector: any): JNative<TElementType> | TElementType | undefined
    {
        if (typeof selector === "string")
        {
            return JNativeHelper.createNextSelector<TElementType>(
                this,
                JNativeHelper.getElements(this)
                    .map(item => Sizzle(selector, item))
                    .reduce((a, b) => a.concat(b), [])
            );
        }

        return super.find(selector);
    }
    finish(): JNative<TElementType> | undefined
    {
        const
            queueName = "default";

        if (!Animator.animationQueues[queueName])
            return;

        for (let element of JNativeHelper.getElements(this))
        {
            const
                animationQueue = Animator.animationQueues[queueName].get(element);

            if (animationQueue && animationQueue.length)
                animationQueue.forEach((item: any) => { item && item.finish && item.finish(); });

            Animator.animationQueues[queueName].delete(element);
        }

        return this;
    }
    focus(eventDataOrHandler?: any | Function, handler?: Function)
    {
        return this._triggerNativeEvent("focus", eventDataOrHandler, handler);
    }
    focusin(eventDataOrHandler?: any | Function, handler?: Function)
    {
        return this._triggerNativeEvent("focusin", eventDataOrHandler, handler);
    }
    focusout(eventDataOrHandler?: any | Function, handler?: Function)
    {
        return this._triggerNativeEvent("focusout", eventDataOrHandler, handler);
    }
    get(index: number)
    {
        if (index < 0)
            index = this.length + index;

        if (index >= 0 && index < this.length)
            return this[index];
    }
    hasAnyClass(classNameArray: string[])
    {
        for (let i = 0; i < classNameArray.length; i++)
        {
            if (this.hasClass(classNameArray[i]))
                return true;
        }

        return false;
    }
    hasClass(className: string)
    {
        if (!className || !this.length)
            return false;

        const
            classNames = className.split(' ').filter(item => !!item);

        if (!classNames.length)
            return false;

        for (let element of JNativeHelper.getElements(this))
        {
            if (!element.classList)
                continue;

            let
                fullMatch = true;

            for (let item of classNames)
            {
                if (!element.classList.contains(item))
                {
                    fullMatch = false;
                    break;
                }
            }

            if (fullMatch)
                return true;
        }

        return false;
    }
    height(): number | undefined;
    height(value: any): this
    height(value?: any): number | this | undefined
    {
        if (value === undefined)
        {
            const
                elements = JNativeHelper.getElements(this);

            if (!elements.length)
                return;

            return parseFloat(getComputedStyle(elements[0]).height);
        }

        if (typeof value === "function")
            value = value();

        for (let element of JNativeHelper.getHtmlElements(this))
            element.style.height = typeof value === "string" ? value : `${value}px`;

        return this;
    }
    hide()
    {
        for (let element of JNativeHelper.getHtmlElements(this))
            element.style.display = "none";

        return this;
    }
    hover(eventDataOrHandler?: any | Function, handler?: Function)
    {
        return this._triggerNativeEvent("hover", eventDataOrHandler, handler);
    }
    html(): string
    html(html: string): this
    html(html?: string): string | this
    {
        if (html === undefined)
        {
            return (
                JNativeHelper.getElements(this)
                    .map(item => item.innerHTML)
                    .reduce((a, b) => a.concat(b), "")
            );
        }

        for (let element of JNativeHelper.getElements(this))
            element.innerHTML = html;

        return this;
    }
    index(arrayOrElement?: JNative | Element)
    {
        if (!arrayOrElement)
        {
            const
                elements = JNativeHelper.getElements(this);

            if (elements.length && elements[0].parentElement)
                return Array.prototype.indexOf.call(elements[0].parentElement.children, elements[0]);

            return -1;
        }

        let
            item: Element | undefined = undefined;

        if (ArrayHelper.isArrayLike(arrayOrElement))
        {
            const
                elements = JNativeHelper.getElements(arrayOrElement);

            if (elements.length)
                item = elements[0];
        }
        else if (arrayOrElement instanceof Element)
            item = arrayOrElement;

        return item ? this.indexOf(item as TElementType) : -1;
    }
    innerHeight(): number | undefined
    innerHeight(valueOrFunction: string | number | ((index: number, height: number) => string | number)): number | undefined
    innerHeight(valueOrFunction?: string | number | ((index: number, height: number) => string | number)): number | undefined
    {
        if (!valueOrFunction)
        {
            const
                elements = JNativeHelper.getElements(this);

            if (!elements.length)
                return;

            return elements[0].clientHeight;
        }

        throw new Error("Not implemented yet");
    }
    innerWidth(valueOrFunction: string | number | ((index: number, height: number) => string | number))
    {
        if (!valueOrFunction)
        {
            const
                elements = JNativeHelper.getElements(this);

            if (!elements.length)
                return;

            return elements[0].clientWidth;
        }

        throw new Error("Not implemented yet");
    }
    insertAfter(arrayOrElementOrSelector: any[] | Element | string)
    {
        if (!this.length)
            return this;

        const
            targetNode = JNativeHelper.getNodeFromArrayOrElementOrSelector(arrayOrElementOrSelector);

        if (targetNode && targetNode.parentElement)
        {
            for (let node of JNativeHelper.getNodes(this))
            {
                if (targetNode.nextSibling)
                    targetNode.parentElement.insertBefore(node, targetNode.nextSibling);
                else
                    targetNode.parentElement.append(node);
            }
        }

        return this;
    }
    insertBefore(arrayOrElementOrSelector: any[] | Element | string)
    {
        if (!this.length)
            return this;

        const
            targetNode = JNativeHelper.getNodeFromArrayOrElementOrSelector(arrayOrElementOrSelector);

        if (targetNode && targetNode.parentElement)
        {
            for (let node of JNativeHelper.getNodes(this))
                targetNode.parentElement.insertBefore(node, targetNode);
        }

        return this;
    }
    is(arrayOrElementOrSelector: Nullable<any[] | Element | string>)
    {
        if (typeof arrayOrElementOrSelector === "string")
            return !!Sizzle.matches(arrayOrElementOrSelector, this.toArray()).length;

        if (ArrayHelper.isArrayLike(arrayOrElementOrSelector))
            return this.length === arrayOrElementOrSelector.length && this.every(item => arrayOrElementOrSelector.indexOf(item) >= 0);

        return this.length === 1 && this[0] === arrayOrElementOrSelector;
    }
    keydown(eventDataOrHandler?: any | Function, handler?: Function)
    {
        return this._triggerNativeEvent("keydown", eventDataOrHandler, handler);
    }
    keypress(eventDataOrHandler?: any | Function, handler?: Function)
    {
        return this._triggerNativeEvent("keypress", eventDataOrHandler, handler);
    }
    keyup(eventDataOrHandler?: any | Function, handler?: Function)
    {
        return this._triggerNativeEvent("keyup", eventDataOrHandler, handler);
    }
    last(): JNative<TElementType>
    {
        return this.length ? JNativeHelper.createNextSelector(this, [this[this.length - 1]]) : JNativeHelper.createNextSelector(this, []);
    }
    mousedown(eventDataOrHandler?: any | Function, handler?: Function)
    {
        return this._triggerNativeEvent("mousedown", eventDataOrHandler, handler);
    }
    mouseenter(eventDataOrHandler?: any | Function, handler?: Function)
    {
        return this._triggerNativeEvent("mouseenter", eventDataOrHandler, handler);
    }
    mouseleave(eventDataOrHandler?: any | Function, handler?: Function)
    {
        return this._triggerNativeEvent("mouseleave", eventDataOrHandler, handler);
    }
    mousemove(eventDataOrHandler?: any | Function, handler?: Function)
    {
        return this._triggerNativeEvent("mousemove", eventDataOrHandler, handler);
    }
    mouseout(eventDataOrHandler?: any | Function, handler?: Function)
    {
        return this._triggerNativeEvent("mouseout", eventDataOrHandler, handler);
    }
    mouseover(eventDataOrHandler?: any | Function, handler?: Function)
    {
        return this._triggerNativeEvent("mouseover", eventDataOrHandler, handler);
    }
    mouseup(eventDataOrHandler?: any | Function, handler?: Function)
    {
        return this._triggerNativeEvent("mouseup", eventDataOrHandler, handler);
    }
    next(arrayOrSelector?: any[] | string): JNative<TElementType>
    {
        return JNativeHelper.createNextSelector(
            this,
            Array.prototype.filter.call(
                JNativeHelper.getElements(this)
                    .map(item =>
                    {
                        if (item.nextElementSibling)
                        {
                            if (!arrayOrSelector)
                                return item.nextElementSibling;

                            if (typeof arrayOrSelector === "string" && item.nextElementSibling.matches(arrayOrSelector))
                                return item.nextElementSibling;

                            if (ArrayHelper.isArrayLike(arrayOrSelector) && arrayOrSelector.indexOf(item.nextElementSibling) > -1)
                                return item.nextElementSibling;
                        }
                    }),
                (item, index, arr) => !!item && arr.indexOf(item) === index
            )
        );
    }
    nextAll(arrayOrSelector?: any[] | string)
    {
        return JNativeHelper.createNextSelector(
            this,
            Array.prototype.filter.call(
                JNativeHelper.getElements(this)
                    .map(item =>
                    {
                        let
                            elementSibling = item.nextElementSibling;

                        if (!arrayOrSelector)
                            return elementSibling;

                        while (elementSibling)
                        {
                            if (typeof arrayOrSelector === "string" && elementSibling.matches(arrayOrSelector))
                                return elementSibling;

                            if (ArrayHelper.isArrayLike(arrayOrSelector) && arrayOrSelector.indexOf(elementSibling) > -1)
                                return elementSibling;

                            elementSibling = elementSibling.nextElementSibling
                        }
                    }),
                (item, index, arr) => !!item && arr.indexOf(item) === index
            )
        );
    }
    not(arrayOrNodeOrSelector: JNative | any[] | Node | string)
    {
        if (typeof arrayOrNodeOrSelector === "string")
            arrayOrNodeOrSelector = this.filter(arrayOrNodeOrSelector);

        if (arrayOrNodeOrSelector instanceof Node)
            return JNativeHelper.createNextSelector(this, Array.prototype.filter.call(this, item => item !== arrayOrNodeOrSelector));
        else if (ArrayHelper.isArrayLike(arrayOrNodeOrSelector))
        {
            const
                elements = JNativeHelper.getNodes(arrayOrNodeOrSelector);

            return JNativeHelper.createNextSelector(this, Array.prototype.filter.call(this, item => elements.indexOf(item) < 0));
        }

        return this;
    }
    off(): JNative<TElementType>;
    off(events: string): JNative<TElementType>;
    off(events: string, handler: Function): JNative<TElementType>;
    off(events: string, selector: string, handler: Function): JNative<TElementType>;
    off(events: string, data: any, handler: Function): JNative<TElementType>;
    off(events: string, selector: string, data: any, handler: Function): JNative<TElementType>;
    off(...args: any[]): JNative<TElementType>
    {
        let
            events,
            selector,
            data,
            handler;

        switch (args.length)
        {
            case 0: // Remove all events
                for (let node of this)
                {
                    const
                        element = JNativeHelper.getElement(node);

                    if (element)
                    {
                        ElementEventManager.eventHandlerOffAll(element);
                        continue;
                    }

                    ObjectEventManager.eventHandlerOffAll(node);
                }

                return this;
            case 1: // events
                events = args[0];
                break;
            case 2: // events, handler
                events = args[0];
                handler = args[1];
                break;
            case 3:
                if (typeof args[1] === "string")
                {
                    // events, selector, handler
                    events = args[0];
                    selector = args[1];
                    handler = args[2];
                }
                else
                {
                    // events, data, handler
                    events = args[0];
                    data = args[1];
                    handler = args[2];
                }
                break;
            case 4: // events, selector, data, handler
                events = args[0];
                selector = args[1];
                data = args[2];
                handler = args[3];
                break;
        }

        const
            eventNames = events.split(" ").filter((item: any) => !!item);

        for (let eventName of eventNames)
        {
            const
                specialEvent = JNativeStatic.event.special[eventName];

            for (let node of this)
            {
                if (node instanceof EventTarget)
                    ElementEventManager.eventHandlerOff(node, eventName, selector, data, handler);
                else
                    ObjectEventManager.eventHandlerOff(node, eventName, selector, data, handler);

                if (specialEvent)
                {
                    if (specialEvent.teardown)
                        specialEvent.teardown.call(node);

                    continue;
                }
            }
        }

        return this;
    }
    offset()
    {
        const
            elements = JNativeHelper.getElements(this);

        if (!elements.length || !elements[0].getBoundingClientRect)
            return;

        const
            rect = elements[0].getBoundingClientRect();

        //return {
        //    top: rect.top + window.pageYOffset - document.documentElement.clientTop,
        //    left: rect.left + window.pageXOffset - document.documentElement.clientLeft
        //};

        return {
            top: rect.top + document.body.scrollTop,
            left: rect.left + document.body.scrollLeft
        };
    }
    offsetParent()
    {
        const
            elements = JNativeHelper.getHtmlElements(this);

        if (!elements.length)
            return;

        return JNativeHelper.createNextSelector(
            this,
            [elements[0].offsetParent || elements[0]]
        );
    }
    on(): JNative<TElementType>;
    on(events: string): JNative<TElementType>;

    on(events: string, handler: Function): JNative<TElementType>;

    on(events: string, handler: Function, thisArg: any): JNative<TElementType>;
    on(events: string, selector: string, handler: Function): JNative<TElementType>;
    on(events: string, data: any, handler: Function): JNative<TElementType>;

    on(events: string, selector: string, handler: Function, thisArg: any): JNative<TElementType>;
    on(events: string, data: any, handler: Function, thisArg: any): JNative<TElementType>;
    on(events: string, selector: string, data: any, handler: Function): JNative<TElementType>;

    on(events: string, selector: string, data: any, handler: Function, thisArg: any): JNative<TElementType>;

    on(...args: any[]): JNative<TElementType>
    {
        let
            events,
            selector,
            data,
            handler,
            thisArg;

        switch (args.length)
        {
            case 0:
                return this;
            case 1: // events
                events = args[0];
                break;
            case 2: // events, handler
                events = args[0];
                handler = args[1];
                break;
            case 3:
                if (typeof args[1] === "function")
                {
                    // events, handler, thisArg
                    events = args[0];
                    handler = args[1];
                    thisArg = args[2];
                }
                else if (typeof args[1] === "string")
                {
                    // events, selector, handler
                    events = args[0];
                    selector = args[1];
                    handler = args[2];
                }
                else
                {
                    // events, data, handler
                    events = args[0];
                    data = args[1];
                    handler = args[2];
                }
                break;
            case 4:
                if (typeof args[1] === "string")
                {
                    if (typeof args[3] === "function")
                    {
                        // events, selector, data, handler
                        events = args[0];
                        selector = args[1];
                        data = args[2];
                        handler = args[3];
                    }
                    else
                    {
                        // events, selector, handler, thisArg
                        events = args[0];
                        selector = args[1];
                        handler = args[2];
                        thisArg = args[3];
                    }
                }
                else
                {
                    // events, data, handler, thisArg
                    events = args[0];
                    data = args[1];
                    handler = args[2];
                    thisArg = args[3];
                }
                break;
            case 5: // events, selector, data, handler, thisArg
                events = args[0];
                selector = args[1];
                data = args[2];
                handler = args[3];
                thisArg = args[4];
                break;
        }

        const
            eventNames = events.split(" ").filter((item: any) => !!item);

        for (let eventName of eventNames)
        {
            const
                specialEvent = JNativeStatic.event.special[eventName];

            for (let node of this)
            {
                if (node instanceof EventTarget)
                    ElementEventManager.eventHandlerOn(node, eventName, selector, data, handler, false, thisArg);
                else
                    ObjectEventManager.eventHandlerOn(node, eventName, selector, data, handler, false, thisArg);

                if (specialEvent)
                {
                    if (specialEvent.setup)
                        specialEvent.setup.call(node);

                    continue;
                }
            }
        }

        return this;
    }
    one(): JNative<TElementType>;
    one(events: string): JNative<TElementType>;

    one(events: string, handler: Function): JNative<TElementType>;

    one(events: string, handler: Function, thisArg: any): JNative<TElementType>;
    one(events: string, selector: string, handler: Function): JNative<TElementType>;
    one(events: string, data: any, handler: Function): JNative<TElementType>;

    one(events: string, selector: string, handler: Function, thisArg: any): JNative<TElementType>;
    one(events: string, data: any, handler: Function, thisArg: any): JNative<TElementType>;
    one(events: string, selector: string, data: any, handler: Function): JNative<TElementType>;

    one(events: string, selector: string, data: any, handler: Function, thisArg: any): JNative<TElementType>;

    one(...args: any[]): JNative<TElementType>
    {
        let
            events,
            selector,
            data,
            handler,
            thisArg;

        switch (args.length)
        {
            case 0:
                return this;
            case 1: // events
                events = args[0];
                break;
            case 2: // events, handler
                events = args[0];
                handler = args[1];
                break;
            case 3:
                if (typeof args[1] === "function")
                {
                    // events, handler, thisArg
                    events = args[0];
                    handler = args[1];
                    thisArg = args[2];
                }
                else if (typeof args[1] === "string")
                {
                    // events, selector, handler
                    events = args[0];
                    selector = args[1];
                    handler = args[2];
                }
                else
                {
                    // events, data, handler
                    events = args[0];
                    data = args[1];
                    handler = args[2];
                }
                break;
            case 4:
                if (typeof args[1] === "string")
                {
                    if (typeof args[3] === "function")
                    {
                        // events, selector, data, handler
                        events = args[0];
                        selector = args[1];
                        data = args[2];
                        handler = args[3];
                    }
                    else
                    {
                        // events, selector, handler, thisArg
                        events = args[0];
                        selector = args[1];
                        handler = args[2];
                        thisArg = args[3];
                    }
                }
                else
                {
                    // events, data, handler, thisArg
                    events = args[0];
                    data = args[1];
                    handler = args[2];
                    thisArg = args[3];
                }
                break;
            case 5: // events, selector, data, handler, thisArg
                events = args[0];
                selector = args[1];
                data = args[2];
                handler = args[3];
                thisArg = args[4];
                break;
        }

        const
            eventNames = events.split(" ").filter((item: any) => !!item);

        for (let eventName of eventNames)
        {
            const
                specialEvent = JNativeStatic.event.special[eventName];

            for (let node of this)
            {
                if (node instanceof EventTarget)
                    ElementEventManager.eventHandlerOn(node, eventName, selector, data, handler, true, thisArg);
                else
                    ObjectEventManager.eventHandlerOn(node, eventName, selector, data, handler, true, thisArg);

                if (specialEvent)
                {
                    if (specialEvent.setup)
                        specialEvent.setup.call(node);

                    continue;
                }
            }
        }

        return this;
    }
    outerHeight(): number;
    outerHeight(includeMargin?: boolean): number;
    outerHeight(value?: string | number | boolean, includeMargin?: boolean): number | undefined
    {
        if (typeof value === "boolean")
        {
            includeMargin = value;
            value = undefined;
        }

        if (value === undefined)
        {
            const
                elements = JNativeHelper.getHtmlElements(this);

            if (!elements.length)
                return;

            if (!includeMargin)
                return elements[0].offsetHeight;

            const
                style = getComputedStyle(elements[0]);

            return elements[0].offsetHeight + parseFloat(style.marginTop) + parseFloat(style.marginBottom);
        }

        throw new Error("Not implemented yet");
    }
    outerWidth(): number;
    outerWidth(includeMargin?: boolean): number;
    outerWidth(value?: string | number | boolean, includeMargin?: boolean): number | undefined
    {
        if (typeof value === "boolean")
        {
            includeMargin = value;
            value = undefined;
        }

        if (value === undefined)
        {
            const
                elements = JNativeHelper.getHtmlElements(this);

            if (!elements.length)
                return;

            if (!includeMargin)
                return elements[0].offsetWidth;

            const
                style = getComputedStyle(elements[0]);

            return elements[0].offsetWidth + parseFloat(style.marginLeft) + parseFloat(style.marginRight);
        }

        throw new Error("Not implemented yet");
    }
    parent(): JNative<TElementType>
    {
        return JNativeHelper.createNextSelector(
            this,
            Array.prototype.filter.call(JNativeHelper.getElements(this).map(item => item.parentElement), item => !!item)
        );
    }
    parents(selector?: string): JNative<TElementType>
    {
        return JNativeHelper.createNextSelector(
            this,
            Array.prototype.filter.call(
                JNativeHelper.getElements(this)
                    .map(item =>
                    {
                        const
                            parents: any[] = [];

                        let
                            parentElement = item.parentElement;

                        while (parentElement)
                        {
                            if (!selector || parentElement.matches(selector))
                                parents.push(parentElement);

                            parentElement = parentElement.parentElement;
                        }

                        return parents;
                    })
                    .reduce((a, b) => a.concat(b), []),
                (item, index, arr) => arr.indexOf(item) === index
            )
        );
    }
    position(): { left?: number; top?: number; }
    {
        if (!this.length)
            return { left: undefined, top: undefined };

        const
            offset = this.offset(),
            parentOffset = this.offsetParent()?.offset();

        if (!offset || !parentOffset)
        {
            return { left: 0, top: 0 }
        }

        return {
            left: offset.left - parentOffset.left,
            top: offset.top - parentOffset.top
        };
    }

    prepend(arrayOrElementHtmlOrSelector?: Nullable<ElementType[] | ElementType | string>): JNative<TElementType>
    {
        if (arrayOrElementHtmlOrSelector)
        {
            if (ArrayHelper.isArrayLike(arrayOrElementHtmlOrSelector) || arrayOrElementHtmlOrSelector instanceof Node)
            {
                const
                    array = ArrayHelper.isArrayLike(arrayOrElementHtmlOrSelector) ? arrayOrElementHtmlOrSelector : [arrayOrElementHtmlOrSelector],
                    elements = JNativeHelper.getElements(this),
                    lastElement = elements.length ? elements[elements.length - 1] : undefined;

                if (lastElement)
                    lastElement.prepend.apply(lastElement, JNativeHelper.getNodes(array));
            }
            else if (typeof arrayOrElementHtmlOrSelector === "string")
            {
                const
                    htmlOrSelector = arrayOrElementHtmlOrSelector;

                for (let element of JNativeHelper.getElements(this))
                    element.prepend.apply(element, $$(htmlOrSelector) as unknown as Element[]);
            }
        }

        return this;
    }
    prependTo(arrayOrElementOrSelector?: Nullable<any[] | Element | string>): JNative<TElementType>
    {
        if (!arrayOrElementOrSelector)
            return this;

        if (!this.length)
            return this;

        const
            element = JNativeHelper.getElementFromArrayOrElementOrSelector(arrayOrElementOrSelector);

        if (element)
        {
            element
                .prepend
                .apply(
                    element,
                    JNativeHelper.getNodes(this)
                );
        }

        return this;
    }
    prev(arrayOrSelector?: any[] | string): JNative<TElementType>
    {
        return JNativeHelper.createNextSelector(
            this,
            Array.prototype.filter.call(
                JNativeHelper.getElements(this)
                    .map(item =>
                    {
                        if (item.previousElementSibling)
                        {
                            if (!arrayOrSelector)
                                return item.previousElementSibling;

                            if (typeof arrayOrSelector === "string" && item.previousElementSibling.matches(arrayOrSelector))
                                return item.previousElementSibling;

                            if (ArrayHelper.isArrayLike(arrayOrSelector) && arrayOrSelector.indexOf(item.previousElementSibling) > -1)
                                return item.previousElementSibling;
                        }
                    }),
                (item, index, arr) => !!item && arr.indexOf(item) === index
            )
        );
    }
    prevAll(arrayOrSelector?: any[] | string)
    {
        return JNativeHelper.createNextSelector(
            this,
            Array.prototype.filter.call(
                JNativeHelper.getElements(this)
                    .map(item =>
                    {
                        let
                            elementSibling = item.previousElementSibling;

                        if (!arrayOrSelector)
                            return elementSibling;

                        while (elementSibling)
                        {
                            if (typeof arrayOrSelector === "string" && elementSibling.matches(arrayOrSelector))
                                return elementSibling;

                            if (ArrayHelper.isArrayLike(arrayOrSelector) && arrayOrSelector.indexOf(elementSibling) > -1)
                                return elementSibling;

                            elementSibling = elementSibling.previousElementSibling
                        }

                        return;
                    }),
                (item, index, arr) => !!item && arr.indexOf(item) === index
            )
        );
    }
    promise()
    {
        return this._promise || Promise.resolve();
    }
    prop(propertyName: keyof TElementType, value?: any)
    {
        const
            elements = JNativeHelper.getHtmlElements(this);

        if (value === undefined)
            return elements.length ? (elements[0] as any)[propertyName] : null;

        for (let element of elements)
            (element as any)[propertyName] = value;

        return this;
    }
    radControl(control?: any)
    {
        if (!control)
        {
            if (!this.length)
                return;

            if (!(this[0] instanceof Element))
                return;

            return JNativeStatic.radControl(this[0]);
        }

        if (this.length && this[0] instanceof Element)
            JNativeStatic.radControl(this[0], control);

        return this;
    }
    radControls(selector: string)
    {
        return (
            Array.prototype.filter.call(
                $$(JNativeHelper.getElements(this))
                    .find(selector)
                    .map(item => JNativeStatic.radControl(item)),
                item => !!item
            )
        );
    }
    ready(fn: () => void): JNative<TElementType>
    {
        if (typeof fn === "function")
        {
            if (document.readyState === "loading")
                ElementEventManager.documentReadyEventHandlers.push(fn);
            else
                setTimeout(() => { fn() });
        }

        return this;
    }
    remove()
    {
        for (let element of JNativeHelper.getElements(this))
        {
            $$(element)
                .add(element.querySelectorAll("*"))
                .off()
                .removeData()
                .stop(true, true);

            if (element.parentElement)
                element.parentElement.removeChild(element);
        }

        return this;
    }
    removeAttr(attrName: string)
    {
        for (let element of JNativeHelper.getElements(this))
            element.removeAttribute(attrName);

        return this;
    }
    removeClass(className?: string)
    {
        for (let element of JNativeHelper.getElements(this))
        {
            if (!className)
            {
                if (element.className)
                    element.className = "";

                continue;
            }

            if ((element.classList as any)?.remove)
            {
                DOMTokenList.prototype.remove.apply(
                    element.classList,
                    className
                        .split(" ")
                        .filter(item => !!item)
                );
                continue;
            }

            const
                originalClassName = element.className;

            let
                classList =
                    originalClassName
                        ? originalClassName.split(" ").filter(item => !!item)
                        : [];

            if (className)
            {
                const
                    classNames =
                        className
                            .split(" ")
                            .filter(item => !!item);

                classList =
                    classList
                        .filter(item => !classNames.some(itemInner => itemInner === item));
            }

            const
                currentClassName = classList.join(" ");

            if (currentClassName !== originalClassName)
                element.className = currentClassName;
        }

        return this;
    }
    removeData(arrayOrName?: string[] | string)
    {
        if (typeof arrayOrName === "string")
            arrayOrName = [arrayOrName];

        for (let element of JNativeHelper.getElements(this))
        {
            if (!arrayOrName)
                JNativeHelper.dataMap.delete(element);
            else
            {
                const
                    data = JNativeHelper.dataMap.get(element);

                if (data)
                {
                    for (let name of arrayOrName)
                        delete data[name];
                }
            }
        }

        return this;
    }
    removeRadControl()
    {
        for (let node of JNativeHelper.getElements(this))
            JNativeHelper.radControlMap.delete(node);

    }
    resize(eventDataOrHandler?: any | Function, handler?: Function)
    {
        return this._triggerNativeEvent("resize", eventDataOrHandler, handler);
    }
    scroll(eventDataOrHandler?: any | Function, handler?: Function)
    {
        return this._triggerNativeEvent("scroll", eventDataOrHandler, handler);
    }
    scrollLeft(): number | undefined;
    scrollLeft(value: number): JNative<TElementType>;
    scrollLeft(value?: number): JNative<TElementType> | number | undefined
    {
        if (value === undefined)
        {
            if (!this.length)
                return;

            const
                elements = JNativeHelper.getElements(this);

            if (!elements.length)
                return;

            return elements[0].scrollLeft;
        }

        for (let element of JNativeHelper.getElements(this))
            element.scrollLeft = value;

        return this;
    }
    scrollTop(): number | undefined;
    scrollTop(value: number): JNative<TElementType>;
    scrollTop(value?: number): JNative<TElementType> | number | undefined
    {
        if (value === undefined)
        {
            if (!this.length)
                return;

            const
                elements = JNativeHelper.getElements(this);

            if (!elements.length)
                return;

            return elements[0].scrollTop;
        }

        for (let element of JNativeHelper.getElements(this))
            element.scrollTop = value;

        return this;
    }
    select(eventDataOrHandler?: any | Function, handler?: Function)
    {
        return this._triggerNativeEvent("select", eventDataOrHandler, handler);
    }
    show()
    {
        for (let element of JNativeHelper.getHtmlElements(this))
            element.style.display = "";

        return this;
    }
    slideDown(duration?: number, complete?: Function): JNative<TElementType>;
    slideDown(duration?: number, completeOrEasing?: string | Function, complete?: Function): JNative<TElementType>
    {
        let
            easing;

        if (typeof completeOrEasing === "string")
            easing = completeOrEasing;
        else if (typeof completeOrEasing === "function")
            complete = completeOrEasing;

        return this.animate({ height: 200 }, duration, easing, complete);
    }
    slideUp(duration?: number, complete?: Function): JNative<TElementType>;
    slideUp(duration?: number, completeOrEasing?: string | Function, complete?: Function): JNative<TElementType>
    {
        let
            easing;

        if (typeof completeOrEasing === "string")
            easing = completeOrEasing;
        else if (typeof completeOrEasing === "function")
            complete = completeOrEasing;

        return this.animate({ height: 0 }, duration, easing, complete);
    }
    stop(): JNative<TElementType>;
    stop(clearQueue: boolean): JNative<TElementType>;
    stop(clearQueue: boolean, jumpToEnd: boolean): JNative<TElementType>;
    stop(queue: string): JNative<TElementType>;
    stop(queue: string, clearQueue: boolean): JNative<TElementType>;
    stop(queue: string, clearQueue: boolean, jumpToEnd: boolean): JNative<TElementType>;
    stop(queueOrClearQueue?: string | boolean, clearQueueOrJumpToEnd?: boolean, jumpToEnd?: boolean): JNative<TElementType>
    {
        let
            queue = "default",
            clearQueue = false;

        if (typeof queueOrClearQueue === "boolean")
        {
            clearQueue = queueOrClearQueue;
            jumpToEnd = clearQueueOrJumpToEnd;
        }
        else if (typeof queueOrClearQueue === "string")
        {
            queue = queueOrClearQueue;

            if (clearQueueOrJumpToEnd !== undefined)
                clearQueue = clearQueueOrJumpToEnd;
        }

        if (!(Animator.animationQueues as any)[queue])
            return this;

        for (let node of this)
        {
            const
                animationQueue = (Animator.animationQueues as any)[queue].get(node);

            if (animationQueue && animationQueue.length)
            {
                animationQueue.forEach((item: Animator) =>
                {
                    if (jumpToEnd)
                        item.finish();
                    else
                        item.stop();
                });
            }

            if (clearQueue)
                (Animator.animationQueues as any)[queue].delete(node);
        }

        return this;
    }
    submit(eventDataOrHandler?: any | Function, handler?: Function)
    {
        return this._triggerNativeEvent("submit", eventDataOrHandler, handler);
    }
    text(): string
    text(value: Nullable<string>): this
    text(value?: Nullable<string>): string | this
    {
        if (value === undefined)
        {
            return (
                JNativeHelper.getElements(this)
                    .map(item => item.textContent ?? "")
                    .reduce((a, b) => a.concat(b), "")
            );
        }

        for (let element of JNativeHelper.getElements(this))
            element.textContent = value ?? "";

        return this;
    }
    toArray(): any[]
    {
        return Array.from(this);
    }
    trigger(eventName: string, eventData?: any)
    {
        for (let item of this)
        {
            if (item instanceof EventTarget)
            {
                ElementEventManager.eventHandlerTrigger(item, eventName, eventData);
                continue;
            }

            ObjectEventManager.eventHandlerTrigger(item, eventName, eventData, true);
        }

        return this;
    }
    triggerHandler(eventName: string, eventData?: any)
    {
        for (let item of this)
        {
            if (item instanceof EventTarget)
            {
                ElementEventManager.eventHandlerTriggerHandler(item, eventName, eventData);
                continue;
            }

            ObjectEventManager.eventHandlerTrigger(item, eventName, eventData, false);
        }

        return this;
    }
    unwrap(selector?: string)
    {
        const
            parentElements = new Set<Element>();

        for (let element of JNativeHelper.getElements(this))
        {
            if (!element.parentElement)
                continue;

            if (selector && !element.parentElement.matches(selector))
                continue;

            parentElements.add(element.parentElement);

            const
                grandParentElement = element.parentElement.parentElement;

            if (!grandParentElement)
                continue;

            grandParentElement.insertBefore(element, element.parentElement);
        }

        for (let element of parentElements)
            element.remove();

        return this;
    }
    val(): string | undefined
    val(value: any): this
    val(value?: any): string | this | undefined
    {
        if (value === undefined)
        {
            if (!this.length || (!(this[0] instanceof HTMLInputElement) && !(this[0] instanceof HTMLTextAreaElement)))
                return;

            return this[0].value;
        }

        if (value === null)
            value = "";

        if (typeof value !== "string")
            value = value.toString();

        for (let element of JNativeHelper.getElements(this))
        {
            if (!(element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement))
                continue;

            element.value = value;
        }

        return this;
    }
    width(): number | undefined;
    width(value: any): this
    width(value?: any): number | this | undefined
    {
        if (value === undefined)
        {
            const
                elements = JNativeHelper.getElements(this);

            if (!elements.length)
                return;

            return parseFloat(getComputedStyle(elements[0]).width);
        }

        if (typeof value === "function")
            value = value();

        for (let element of JNativeHelper.getHtmlElements(this))
            element.style.width = typeof value === "string" ? value : `${value}px`;

        return this;
    }
    wrap(arrayOrElementOrFunctionOrHtmlOrSelector: ElementType[] | ElementType | (() => ElementType[]) | string)
    {
        if (this.length && arrayOrElementOrFunctionOrHtmlOrSelector)
        {
            for (let element of JNativeHelper.getElements(this))
            {
                let
                    wrapSelection = arrayOrElementOrFunctionOrHtmlOrSelector;

                if (typeof wrapSelection === "function")
                    wrapSelection = (wrapSelection as () => ElementType[]).call(element);

                if (typeof wrapSelection === "string")
                    wrapSelection = $$(wrapSelection);

                let
                    wrapElement: Element | undefined;

                if (wrapSelection instanceof Element)
                    wrapElement = wrapSelection;
                else if (ArrayHelper.isArrayLike(wrapSelection) && wrapSelection.length && wrapSelection[0] instanceof Element)
                    wrapElement = wrapSelection[0];

                if (wrapElement)
                {
                    if (element.parentElement)
                        element.parentElement.insertBefore(wrapElement, element);

                    while (wrapElement?.firstElementChild)
                        wrapElement = wrapElement.firstElementChild;

                    wrapElement.append(element);
                }
            }
        }

        return this;
    }
    wrapAll(arrayOrElementOrFunctionOrHtmlOrSelector: ElementType[] | ElementType | (() => ElementType[]) | string)
    {
        const
            elements = JNativeHelper.getElements(this);

        if (elements.length && arrayOrElementOrFunctionOrHtmlOrSelector)
        {
            let
                wrapSelection = arrayOrElementOrFunctionOrHtmlOrSelector;

            if (typeof wrapSelection === "function")
                wrapSelection = (wrapSelection as () => ElementType[]).call(elements);

            if (typeof wrapSelection === "string")
                wrapSelection = $$(wrapSelection);

            let
                wrapElement: Element | undefined;

            if (wrapSelection instanceof Element)
                wrapElement = wrapSelection;
            else if (ArrayHelper.isArrayLike(wrapSelection) && wrapSelection.length && wrapSelection[0] instanceof Element)
                wrapElement = wrapSelection[0];

            if (wrapElement)
            {
                if (elements[0].parentElement)
                    elements[0].parentElement.insertBefore(wrapElement, elements[0]);

                while (wrapElement?.firstElementChild)
                    wrapElement = wrapElement.firstElementChild;

                for (let element of elements)
                    wrapElement.append(element);
            }
        }

        return this;
    }
}

class JNativeStaticHelper
{
    public static c1 = 1.70158;
    public static c2 = JNativeStaticHelper.c1 * 1.525;
    public static c3 = JNativeStaticHelper.c1 + 1;
    public static c4 = (2 * Math.PI) / 3;
    public static c5 = (2 * Math.PI) / 4.5;

    public static bounceOut(x: number)
    {
        const
            n1 = 7.5625,
            d1 = 2.75;

        if (x < 1 / d1)
            return n1 * x * x;
        else if (x < 2 / d1)
            return n1 * (x -= 1.5 / d1) * x + 0.75;
        else if (x < 2.5 / d1)
            return n1 * (x -= 2.25 / d1) * x + 0.9375;
        else
            return n1 * (x -= 2.625 / d1) * x + 0.984375;
    }
}

export class AjaxResponse
{
    public static classNamespace = NAMESPACE + "JNative";
    public static className = "AjaxResponse";
    public static get classFullName() { return `${this.classNamespace}.${this.className}`; }

    response: any;
    xhr: XMLHttpRequest;

    constructor(response: any, xhr: XMLHttpRequest)
    {
        this.response = response;
        this.xhr = xhr;
    }
}

export class JNativeStatic
{
    public static classNamespace = NAMESPACE + "JNative";
    public static className = "JNativeStatic";
    public static get classFullName() { return `${this.classNamespace}.${this.className}`; }

    private static _special: any = {};

    public static easing = {
        linear: (x: number) => x,
        easeInQuad: (x: number) => x * x,
        easeOutQuad: (x: number) => 1 - (1 - x) * (1 - x),
        easeInOutQuad: (x: number) => x < 0.5 ? 2 * x * x : 1 - Math.pow(-2 * x + 2, 2) / 2,
        easeInCubic: (x: number) => x * x * x,
        easeOutCubic: (x: number) => 1 - Math.pow(1 - x, 3),
        easeInOutCubic: (x: number) => x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2,
        easeInQuart: (x: number) => x * x * x * x,
        easeOutQuart: (x: number) => 1 - Math.pow(1 - x, 4),
        easeInOutQuart: (x: number) => x < 0.5 ? 8 * x * x * x * x : 1 - Math.pow(-2 * x + 2, 4) / 2,
        easeInQuint: (x: number) => x * x * x * x * x,
        easeOutQuint: (x: number) => 1 - Math.pow(1 - x, 5),
        easeInOutQuint: (x: number) => x < 0.5 ? 16 * x * x * x * x * x : 1 - Math.pow(-2 * x + 2, 5) / 2,
        easeInSine: (x: number) => 1 - Math.cos((x * Math.PI) / 2),
        easeOutSine: (x: number) => Math.sin((x * Math.PI) / 2),
        easeInOutSine: (x: number) => -(Math.cos(Math.PI * x) - 1) / 2,
        easeInExpo: (x: number) => x === 0 ? 0 : Math.pow(2, 10 * x - 10),
        easeOutExpo: (x: number) => x === 1 ? 1 : 1 - Math.pow(2, -10 * x),
        easeInOutExpo: (x: number) =>
        (
            x === 0
                ? 0
                : x === 1
                    ? 1
                    : x < 0.5
                        ? Math.pow(2, 20 * x - 10) / 2
                        : (2 - Math.pow(2, -20 * x + 10)) / 2
        ),
        easeInCirc: (x: number) => 1 - Math.sqrt(1 - Math.pow(x, 2)),
        easeOutCirc: (x: number) => Math.sqrt(1 - Math.pow(x - 1, 2)),
        easeInOutCirc: (x: number) =>
        (
            x < 0.5
                ? (1 - Math.sqrt(1 - Math.pow(2 * x, 2))) / 2
                : (Math.sqrt(1 - Math.pow(-2 * x + 2, 2)) + 1) / 2
        ),
        easeInBack: (x: number) => JNativeStaticHelper.c3 * x * x * x - JNativeStaticHelper.c1 * x * x,
        easeOutBack: (x: number) => 1 + JNativeStaticHelper.c3 * Math.pow(x - 1, 3) + JNativeStaticHelper.c1 * Math.pow(x - 1, 2),
        easeInOutBack: (x: number) =>
        (
            x < 0.5
                ? (Math.pow(2 * x, 2) * ((JNativeStaticHelper.c2 + 1) * 2 * x - JNativeStaticHelper.c2)) / 2
                : (Math.pow(2 * x - 2, 2) * ((JNativeStaticHelper.c2 + 1) * (x * 2 - 2) + JNativeStaticHelper.c2) + 2) / 2
        ),
        easeInElastic: (x: number) =>
        (
            x === 0
                ? 0
                : x === 1
                    ? 1
                    : -Math.pow(2, 10 * x - 10) * Math.sin((x * 10 - 10.75) * JNativeStaticHelper.c4)
        ),
        easeOutElastic: (x: number) =>
        (
            x === 0
                ? 0
                : x === 1
                    ? 1
                    : Math.pow(2, -10 * x) * Math.sin((x * 10 - 0.75) * JNativeStaticHelper.c4) + 1
        ),
        easeInOutElastic: (x: number) =>
        (
            x === 0
                ? 0
                : x === 1
                    ? 1
                    : x < 0.5
                        ? -(Math.pow(2, 20 * x - 10) * Math.sin((20 * x - 11.125) * JNativeStaticHelper.c5)) / 2
                        : (Math.pow(2, -20 * x + 10) * Math.sin((20 * x - 11.125) * JNativeStaticHelper.c5)) / 2 + 1
        ),
        easeInBounce: (x: number) => 1 - JNativeStaticHelper.bounceOut(1 - x),
        easeOutBounce: JNativeStaticHelper.bounceOut,
        easeInOutBounce: (x: number) =>
        (
            x < 0.5
                ? (1 - JNativeStaticHelper.bounceOut(1 - 2 * x)) / 2
                : (1 + JNativeStaticHelper.bounceOut(2 * x - 1)) / 2
        )
    };
    public static event = {
        special: this._special
    };
    public static expr = JNativeHelper.jNativeSelectors;
    public static fn = JNative.create();

    public static addClass(element: Element, className: string)
    {
        if (!className)
            return;

        if ((element.classList as any)?.add)
        {
            DOMTokenList.prototype.add.apply(
                element.classList,
                className
                    .split(" ")
                    .filter(item => !!item)
            );
            return;
        }

        const
            originalClassName = element.className,
            classList = originalClassName ? originalClassName.split(" ").filter(item => !!item) : [];

        if (className)
        {
            className
                .split(" ")
                .filter(item => !!item)
                .filter(item => classList.indexOf(item) === -1)
                .forEach(item => { classList.push(item); })
        }

        const
            currentClassName = classList.join(" ");

        if (currentClassName !== originalClassName)
            element.className = currentClassName;
    }
    public static ajax(options: any): Promise<AjaxResponse>
    {
        options = JNativeStatic.extend(true, {}, {
            contentType: "application/x-www-form-urlencoded; charset=utf-8",
            headers: {},
            sendXRequestedWith: true,
            timeout: AjaxSetupOptions.default.timeout,
            xhrFields: {}
        }, options);

        if (options.sendXRequestedWith)
            options.headers["X-Requested-With"] = "XMLHttpRequest";

        if (options.contentType)
            options.headers["Content-Type"] = options.contentType;

        return new Promise<AjaxResponse>((resolve, reject) =>
        {
            const
                xhr = new XMLHttpRequest();

            xhr.onreadystatechange = () =>
            {
                switch (xhr.readyState)
                {
                    case XMLHttpRequest.DONE:

                        $$(document).triggerHandler("ajaxComplete", { options: options, xhr: xhr });

                        if (xhr.status >= 200 && xhr.status < 300)
                        {
                            $$(document).triggerHandler("ajaxSuccess", { options: options, xhr: xhr });

                            resolve(new AjaxResponse(xhr.status === 204 ? undefined : xhr.response, xhr));
                            break;
                        }

                        $$(document).triggerHandler("ajaxError", { options: options, xhr: xhr });
                        reject(xhr);
                        break;
                }
            };

            xhr.open(options.type || "GET", options.url, true);

            if (options.dataType)
                xhr.responseType = options.dataType;

            for (let i = 0, keys = Object.keys(options.headers); i < keys.length; i++)
                xhr.setRequestHeader(keys[i], options.headers[keys[i]]);

            for (let i = 0, keys = Object.keys(options.xhrFields); i < keys.length; i++)
                (xhr as any)[keys[i]] = options.xhrFields[keys[i]];

            // Ajax Setup Options
            xhr.timeout = AjaxSetupOptions.default?.timeout ?? 0;

            if (AjaxSetupOptions.default?.beforeSend)
                AjaxSetupOptions.default.beforeSend(xhr, options);

            $$(document).triggerHandler("ajaxSend", { options: options, xhr: xhr });

            xhr.send(options.data);
        });
    }
    public static ajaxSetup(options: any)
    {
        return JNativeStatic.extend(true, AjaxSetupOptions.default, options);
    }
    public static contains(element: Element, child: Node)
    {
        return element !== child && element.contains(child);
    }
    public static create(nodeName: string, className?: string, text?: string, data?: any): HTMLElement;
    public static create(config: CreateConfig): HTMLElement;
    public static create(configArray: CreateConfig[]): HTMLElement[];
    public static create(nodeNameOrConfigOrConfigArray: string | CreateConfig | CreateConfig[], className?: string, text?: string, data?: any): HTMLElement | HTMLElement[] | Text;
    public static create(nodeNameOrConfigOrConfigArray: string | CreateConfig | CreateConfig[], className?: string, text?: string, data?: any): HTMLElement | HTMLElement[] | Text
    {
        if (typeof nodeNameOrConfigOrConfigArray === "string")
        {
            nodeNameOrConfigOrConfigArray = {
                nodeName: nodeNameOrConfigOrConfigArray,
                attributes: className ? { "class": className } : undefined,
                text: text,
                data: data
            };
        }

        if (ArrayHelper.isArrayLike(nodeNameOrConfigOrConfigArray))
        {
            return (
                nodeNameOrConfigOrConfigArray
                    .map(item => JNativeStatic.create(item))
            );
        }

        const
            config = nodeNameOrConfigOrConfigArray as CreateConfig;

        if (!config.nodeName)
            return JNativeStatic.createText(config.text ?? "");

        const
            element = document.createElement(config.nodeName);

        if (config.attributes)
        {
            Object
                .keys(config.attributes)
                .forEach(
                    key =>
                    {
                        if (StringHelper.isNullOrUndefined(config.attributes[key]))
                            element.removeAttribute(key);
                        else
                            element.setAttribute(key, config.attributes[key]);
                    }
                );
        }

        if (config.text !== undefined && config.text !== null)
            element.appendChild(JNativeStatic.createText(config.text));

        if (config.data)
            JNativeStatic.data(element, config.data);

        if (config.children)
        {
            config
                .children
                .forEach(item => { element.append(item instanceof Node ? item : JNativeStatic.create(item) as Element); });
        }

        return element;
    }
    public static create$$(nodeName: string, className?: Nullable<string>, text?: Nullable<string>, data?: any): JNative<HTMLElement>;
    public static create$$(config: CreateConfig): JNative<HTMLElement>;
    public static create$$(configArray: CreateConfig[]): JNative<HTMLElement>;
    public static create$$(nodeNameOrConfigOrConfigArray: string | CreateConfig | CreateConfig[], className?: string, text?: string, data?: any): JNative<HTMLElement>;
    public static create$$(nodeNameOrConfigOrConfigArray: string | CreateConfig | CreateConfig[], className?: string, text?: string, data?: any): JNative<HTMLElement | Text>
    {
        return $$(JNativeStatic.create(nodeNameOrConfigOrConfigArray, className, text, data));
    }
    public static createNbsp(): Text
    {
        return JNativeStatic.createText("\u00A0");
    }
    public static createText(text: string)
    {
        return document.createTextNode(text);
    }
    public static data(element: Element): undefined
    public static data(element: Element, key: string): undefined
    public static data(element: Element, value: any): undefined
    public static data(element: Element, key: string, value: any): undefined
    public static data(element: Element, keyOrValue?: any, value?: any): undefined
    {
        let
            key;

        if (typeof keyOrValue === "string")
            key = keyOrValue;
        else
            value = keyOrValue;

        if (typeof key === "string")
            key = StringHelper.fromDashed(key);

        let
            data = JNativeHelper.dataMap.get(element);

        if (!data)
        {
            data = {};
            JNativeHelper.dataMap.set(element, data);

            const
                dataTagStart = "data-",
                attributeNames = element.getAttributeNames().filter(item => item.substring(0, 5) === dataTagStart);

            if (attributeNames.length)
            {
                attributeNames.forEach(item =>
                {
                    let
                        attributeValue = element.getAttribute(item);

                    if (attributeValue && (attributeValue[0] === "[" || attributeValue[0] === "{"))
                    {
                        try { attributeValue = ObjectHelper.fromJson(attributeValue); }
                        catch (e) { /* Intentionally empty */ }
                    }

                    data[StringHelper.fromDashed(item.substring(dataTagStart.length))] = attributeValue;
                });
            }
        }

        if (value === undefined)
            return key ? data[key] : data;

        if (key)
        {
            data[key] = value;
            return;
        }

        for (let property in value)
            data[StringHelper.fromDashed(property)] = value[property];
    }
    public static each(arrayOrObject: any, callback: (index: number, element: any) => boolean | undefined | void)
    {
        if (!arrayOrObject)
            return;

        if (typeof callback !== "function")
            return;

        if (ArrayHelper.isArrayLike(arrayOrObject))
        {
            for (let i = 0; i < arrayOrObject.length; i++)
            {
                if (callback.call(arrayOrObject[i], i, arrayOrObject[i]) === false)
                    return;
            }
            return;
        }

        for (let i = 0, keys = Object.keys(arrayOrObject); i < keys.length; i++)
        {
            if (callback.call(keys[i], i, keys[i]) === false)
                return;
        }
    }
    public static extend(target: any, object1: any, ...objectN: any[]): any
    public static extend(deep: boolean, target: any, object1?: any, ...objectN: any[]): any
    public static extend(deepOrTarget: any, targetOrObject1: any, object1OrObjects: any, objectN: any[]): any
    {
        return ObjectHelper.extend(deepOrTarget, targetOrObject1, object1OrObjects, objectN);
    }
    public static get(url: string, data?: any, success?: (data: any, textStatus: string, xhr: XMLHttpRequest) => void, dataType?: string)
    {
        return this.ajax({
            url: url,
            data: data,
            success: success,
            dataType: dataType
        });
    }
    public static hasClass(element: Element, className: string): boolean
    {
        if (!className)
            return false;

        if (element.classList)
            return element.classList.contains(className);

        const
            originalClassName = element.className,
            classList =
                originalClassName
                    ? originalClassName.split(" ").filter(item => !!item)
                    : [];

        return classList.indexOf(className) > -1;
    }
    public static isPlainObject(obj: any)
    {
        return ObjectHelper.isPlainObject(obj);
    }
    public static radControl(element: Element, control?: any)
    {
        if (!control)
            return JNativeHelper.radControlMap.get(element);

        JNativeStatic.addClass(element, "rad-control");
        JNativeHelper.radControlMap.set(element, control);
    }
}

for (let key of Object.keys(JNativeStatic))
    ($$ as any)[key] = JNativeStatic[key as keyof JNativeStatic];

(window as any).$$ = $$;